import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  IndexTable,
  SkeletonBodyText,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import AppPageFrame from "../../components/layout/AppPageFrame";
import useSupportResource from "../../hooks/useSupportResource";
import { getSupportOverview } from "../../services/supportApi";
import { isSupportAccessDenied } from "../../util/supportAccess";
import SyncEngineHealthPanel from "../support/SyncEngineHealthPanel";
import {
  firstArray,
  formatCount,
  formatDateTime,
  formatDuration,
  formatPercent,
  formatRelativeTime,
  merchantScreenLabel,
  parseSupportDate,
  readableStatus,
  statusTone,
  storeBilling,
  storeDomain,
  storeHealth,
  storeId,
  storeName,
  storePipeline,
} from "../support/supportFormatters";
import "./style.css";

const POLL_INTERVAL_MS = 30_000;

const StatusBadge = ({ status, fallback = "Unknown" }) => (
  <Badge status={statusTone(status)}>{readableStatus(status, fallback)}</Badge>
);

const MetricCard = ({ label, value, context, tone = "neutral" }) => (
  <Card>
    <div className={`support-metric support-metric--${tone}`}>
      <span className="support-metric__label">{label}</span>
      <strong>{value}</strong>
      <span className="support-metric__context">{context}</span>
    </div>
  </Card>
);

const COMPONENT_PRESENTATION = {
  database: {
    label: "Database",
    purpose: "Stores merchant, sync, dispute, and billing operational data.",
  },
  api: {
    label: "Order API",
    purpose: "Receives app traffic and accepts new order-sync work.",
  },
  processor: {
    label: "Sync processor",
    purpose: "Runs queued sync and usage-billing work, including updates sent to PayPal.",
  },
  "merchant-ui": {
    label: "Merchant app",
    purpose: "Embedded Shopify experience used by merchants.",
  },
  "admin-ui": {
    label: "Support admin",
    purpose: "Internal operations and merchant-support workspace.",
  },
};

const componentPresentation = (key, fallback) =>
  COMPONENT_PRESENTATION[String(key || "").toLowerCase()] || {
    label: fallback || key || "Unknown service",
    purpose: "Runtime dependency monitored by PalSync.",
  };

const incidentIdentity = (incident, index = 0) =>
  incident?.id ||
  [
    incident?.code || "INCIDENT",
    incident?.entityType || "system",
    incident?.entityId || index,
  ].join(":");

const incidentAnchorId = (identity) =>
  `incident-${String(identity || "unknown").replace(/[^a-zA-Z0-9_-]+/g, "-")}`;

const componentAnchorId = (key) =>
  `dependency-${String(key || "unknown").replace(/[^a-zA-Z0-9_-]+/g, "-")}`;

const focusTarget = (event, anchorId) => {
  const target = document.getElementById(anchorId);
  if (!target) return;
  event.preventDefault();
  const hash = `#${anchorId}`;
  if (window.location.hash === hash) {
    window.history.replaceState(null, "", hash);
  } else {
    window.history.pushState(null, "", hash);
  }
  const prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "center",
    });
  }
  target.focus();
};

const relatedComponentIncident = (component, incidents) => {
  const key = String(component?.key || component?.name || "").toLowerCase();
  if (component?.incidentId) {
    const direct = incidents.find(
      (incident, index) => incidentIdentity(incident, index) === component.incidentId
    );
    if (direct) return direct;
  }
  return incidents.find(
    (incident) =>
      firstArray(incident?.affectedComponents)
        .map((value) => String(value).toLowerCase())
        .includes(key) ||
      (String(incident?.entityType).toLowerCase() === "component" &&
        String(incident?.entityId).toLowerCase() === key)
  );
};

const Components = ({ components = [], incidents = [] }) => (
  <Card>
    <div className="support-card-content">
      <div className="support-section-heading">
        <div>
          <h2>Dependencies</h2>
          <p>Live service state and probe evidence. Remediation is kept in Active incidents.</p>
        </div>
      </div>
      {components.length === 0 ? (
        <p className="support-empty-copy">No component heartbeat data was returned.</p>
      ) : (
        <div className="support-components" role="list">
          {components.map((component, index) => {
            const key = component?.key || component?.name || component?.component;
            const presentation = componentPresentation(
              key,
              component?.name || component?.component || `Service ${index + 1}`
            );
            const incident = relatedComponentIncident(component, incidents);
            const identity = incident ? incidentIdentity(incident, index) : null;
            return (
              <div
                id={componentAnchorId(key || presentation.label)}
                className="support-component"
                role="listitem"
                tabIndex="-1"
                key={`${key || presentation.label}-${index}`}
              >
                <div className="support-component__heading">
                  <div>
                    <strong>{presentation.label}</strong>
                    <span className="support-component__purpose">{presentation.purpose}</span>
                  </div>
                  <StatusBadge status={component?.status} />
                </div>
                <p className="support-component__evidence">
                  <strong>Evidence:</strong>{" "}
                  {component?.summary || component?.message || "No probe evidence was returned."}
                </p>
                <div className="support-component__meta">
                  {component?.httpStatus !== null && component?.httpStatus !== undefined ? (
                    <span>HTTP {component.httpStatus}</span>
                  ) : null}
                  {component?.latencyMs !== null && component?.latencyMs !== undefined ? (
                    <span>Latency {formatDuration(component.latencyMs)}</span>
                  ) : null}
                  {component?.lastHeartbeatAt ? (
                    <span>Heartbeat {formatRelativeTime(component.lastHeartbeatAt)}</span>
                  ) : null}
                  {component?.checkedAt ? (
                    <span>Checked {formatRelativeTime(component.checkedAt)}</span>
                  ) : null}
                  {component?.backlog !== null && component?.backlog !== undefined ? (
                    <span>Backlog {formatCount(component.backlog)}</span>
                  ) : null}
                  {component?.releaseVersion ? (
                    <span>Release {component.releaseVersion}</span>
                  ) : null}
                </div>
                {identity ? (
                  <a
                    className="support-component__incident-link"
                    href={`#${incidentAnchorId(identity)}`}
                    aria-label={`View ${presentation.label} active incident`}
                    onClick={(event) =>
                      focusTarget(event, incidentAnchorId(identity))
                    }
                  >
                    View active incident
                  </a>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  </Card>
);

const AttentionStores = ({ stores, onOpenStore }) => {
  const rows = stores.map((store, index) => {
    const id = storeId(store);
    const pipeline = storePipeline(store);
    const onboarding = store?.onboarding || {};
    const disputes = store?.disputes || {};
    const paypal = store?.connections?.paypal || {};
    const issue =
      store?.issue ||
      store?.issueSummary ||
      store?.summary ||
      firstArray(store?.issues)?.[0]?.message ||
      "Support review required";
    return (
      <IndexTable.Row id={String(id ?? index)} key={String(id ?? index)} position={index}>
        <IndexTable.Cell>
          <button
            type="button"
            className="support-table-store-link"
            onClick={() => id !== null && onOpenStore(id)}
            disabled={id === null}
          >
            <strong>{storeName(store)}</strong>
            <span>{storeDomain(store)}</span>
          </button>
        </IndexTable.Cell>
        <IndexTable.Cell><StatusBadge status={storeHealth(store)} /></IndexTable.Cell>
        <IndexTable.Cell>
          <StatusBadge status={paypal.connectionHealth || paypal.status} />
          <span className="support-table-note">{formatCount(paypal.activeAccounts)} usable accounts</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {merchantScreenLabel(onboarding, "Unknown screen")}
          <span className="support-table-note">{readableStatus(onboarding.status)}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <span className="support-table-wrap">{issue}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {formatCount(pipeline.queued ?? store?.queued)} queued · {formatCount(
            pipeline.manualReview ?? store?.manualReview
          )} review
        </IndexTable.Cell>
        <IndexTable.Cell>
          {formatCount(disputes.attention)} attention · {formatCount(disputes.overdue)} overdue
        </IndexTable.Cell>
        <IndexTable.Cell>
          {formatRelativeTime(store?.lastSuccessfulSyncAt || store?.lastSyncAt)}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Button onClick={() => id !== null && onOpenStore(id)} disabled={id === null}>
            Open
          </Button>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Card>
      <div className="support-table-card-heading">
        <div>
          <h2>Needs attention</h2>
          <p>Stores ranked by operational impact and age.</p>
        </div>
      </div>
      <IndexTable
        resourceName={{ singular: "store", plural: "stores" }}
        itemCount={stores.length}
        selectable={false}
        headings={[
          { title: "Store" },
          { title: "Health" },
          { title: "PayPal" },
          { title: "Merchant screen" },
          { title: "Primary issue" },
          { title: "Live work" },
          { title: "Disputes" },
          { title: "Last success" },
          { title: "" },
        ]}
      >
        {rows}
      </IndexTable>
      {stores.length === 0 ? (
        <div className="support-table-empty" role="status">
          No stores currently require support attention.
        </div>
      ) : null}
    </Card>
  );
};

const RecentStores = ({ stores, onOpenStore, onViewAll }) => {
  const rows = stores.map((store, index) => {
    const id = storeId(store);
    const onboarding = store?.onboarding || {};
    const paypal = store?.connections?.paypal || {};
    const disputes = store?.disputes || {};
    return (
      <IndexTable.Row id={String(id ?? index)} key={String(id ?? index)} position={index}>
        <IndexTable.Cell>
          <button
            type="button"
            className="support-table-store-link"
            onClick={() => id !== null && onOpenStore(id)}
            disabled={id === null}
          >
            <strong>{storeName(store)}</strong>
            <span>{storeDomain(store)}</span>
          </button>
        </IndexTable.Cell>
        <IndexTable.Cell><StatusBadge status={storeHealth(store)} /></IndexTable.Cell>
        <IndexTable.Cell>
          <StatusBadge status={paypal.connectionHealth || paypal.status} />
          <span className="support-table-note">{formatCount(paypal.activeAccounts)} accounts</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {merchantScreenLabel(onboarding, "Unknown")}
          <span className="support-table-note">{readableStatus(onboarding.status)}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {formatCount(disputes.open)} open
          <span className="support-table-note">{formatCount(disputes.attention)} attention</span>
        </IndexTable.Cell>
        <IndexTable.Cell><StatusBadge status={storeBilling(store)} /></IndexTable.Cell>
        <IndexTable.Cell>{formatDateTime(store?.installedAt || store?.creationDate)}</IndexTable.Cell>
        <IndexTable.Cell>
          <Button onClick={() => id !== null && onOpenStore(id)} disabled={id === null}>
            Open
          </Button>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Card>
      <div className="support-table-card-heading">
        <div>
          <h2>Recent stores</h2>
          <p>Newest installations and their current readiness.</p>
        </div>
        <Button onClick={onViewAll}>View all stores</Button>
      </div>
      <IndexTable
        resourceName={{ singular: "store", plural: "stores" }}
        itemCount={stores.length}
        selectable={false}
        headings={[
          { title: "Store" },
          { title: "Health" },
          { title: "PayPal" },
          { title: "Merchant screen" },
          { title: "Disputes" },
          { title: "Billing" },
          { title: "Installed" },
          { title: "" },
        ]}
      >
        {rows}
      </IndexTable>
      {stores.length === 0 ? (
        <div className="support-table-empty" role="status">No recent stores were returned.</div>
      ) : null}
    </Card>
  );
};

const BillingHealth = ({ billing = {} }) => (
  <Card>
    <div className="support-card-content">
      <div className="support-section-heading">
        <div>
          <h2>Usage billing operations</h2>
          <p>Cutover cohort and ledger readiness. Waived entries are audit-only.</p>
        </div>
        <StatusBadge status={billing.writesMode || "UNKNOWN"} />
      </div>
      <dl className="support-definition-grid">
        <div><dt>Active subscriptions</dt><dd>{formatCount(billing.activeUsageSubscriptions)}</dd></div>
        <div><dt>Legacy fixed · diagnostic</dt><dd>{formatCount(billing.legacyFixedSubscriptions)}</dd></div>
        <div><dt>Stores in trial</dt><dd>{formatCount(billing.trialStores)}</dd></div>
        <div>
          <dt>Prospective eligible stores</dt>
          <dd aria-label="Prospective eligible stores">
            {formatCount(billing.prospectiveEligibleStores)}
          </dd>
        </div>
        <div>
          <dt>Grandfathered stores</dt>
          <dd aria-label="Grandfathered stores">
            {formatCount(billing.grandfatheredStores)}
          </dd>
        </div>
        <div><dt>Pending ledger entries</dt><dd>{formatCount(billing.pendingLedgerEntries)}</dd></div>
        <div><dt>Failed ledger entries</dt><dd>{formatCount(billing.failedLedgerEntries)}</dd></div>
        <div>
          <dt>Reconciliation required</dt>
          <dd aria-label="Reconciliation required">
            {formatCount(billing.reconciliationRequiredLedgerEntries)}
          </dd>
        </div>
        <div>
          <dt>Waived ledger entries</dt>
          <dd aria-label="Waived ledger entries">
            {formatCount(billing.waivedLedgerEntries)}
          </dd>
        </div>
        <div><dt>Last successful charge</dt><dd>{formatDateTime(billing.lastSuccessfulChargeAt)}</dd></div>
      </dl>
    </div>
  </Card>
);

const StoreFleet = ({ stores = {} }) => (
  <Card>
    <div className="support-card-content">
      <div className="support-section-heading">
        <div>
          <h2>Merchant readiness</h2>
          <p>
            Merchant setup and sync readiness. Setup or action counts describe
            merchant configuration, not PalSync service health.
          </p>
        </div>
      </div>
      <dl className="support-definition-grid support-definition-grid--fleet">
        <div><dt>Total</dt><dd>{formatCount(stores.total)}</dd></div>
        <div><dt>Active</dt><dd>{formatCount(stores.active)}</dd></div>
        <div><dt>Sync ready</dt><dd>{formatCount(stores.healthy)}</dd></div>
        <div><dt>Setup or action</dt><dd>{formatCount(stores.attention)}</dd></div>
        <div><dt>Disconnected</dt><dd>{formatCount(stores.disconnected)}</dd></div>
        <div><dt>Inactive</dt><dd>{formatCount(stores.inactive)}</dd></div>
        <div><dt>Trial</dt><dd>{formatCount(stores.trial)}</dd></div>
        <div><dt>New · 24h</dt><dd>{formatCount(stores.new24h)}</dd></div>
        <div><dt>New · 7d</dt><dd>{formatCount(stores.new7d)}</dd></div>
      </dl>
    </div>
  </Card>
);

const ActiveIncidents = ({ incidents = [] }) => {
  const severityRank = { CRITICAL: 4, ERROR: 4, WARNING: 3, DEGRADED: 3, INFO: 2, UNKNOWN: 1 };
  const ranked = [...incidents].sort(
    (left, right) =>
      (severityRank[String(right?.severity || "UNKNOWN").toUpperCase()] || 0) -
      (severityRank[String(left?.severity || "UNKNOWN").toUpperCase()] || 0)
  );
  return (
    <Card>
      <div className="support-card-content">
        <div className="support-section-heading">
          <div><h2>Active incidents</h2><p>Current conditions requiring review.</p></div>
          <Badge status={ranked.length ? statusTone(ranked[0]?.severity) : "success"}>
            {ranked.length ? `${ranked.length} open` : "Clear"}
          </Badge>
        </div>
        {ranked.length ? <ul className="support-incidents">
          {ranked.map((incident, index) => {
            const identity = incidentIdentity(incident, index);
            const affectedComponents = firstArray(incident?.affectedComponents).map(
              (component) => ({
                key: component,
                label: componentPresentation(component).label,
              })
            );
            const scope = `${readableStatus(incident?.entityType || "system")} area${
              incident?.entityId ? ` · ${incident.entityId}` : ""
            }`;
            return (
              <li id={incidentAnchorId(identity)} key={identity} tabIndex="-1">
                <StatusBadge status={incident?.severity || "ATTENTION"} />
                <div>
                  <h3>{incident?.summary || incident?.code || "Operational incident"}</h3>
                  {affectedComponents.length ? (
                    <p className="support-incident__scope">
                      Affected services:{" "}
                      {affectedComponents.map((component, componentIndex) => (
                        <React.Fragment key={component.key}>
                          {componentIndex > 0 ? ", " : ""}
                          <a
                            href={`#${componentAnchorId(component.key)}`}
                            aria-label={`View ${component.label} dependency evidence`}
                            onClick={(event) =>
                              focusTarget(event, componentAnchorId(component.key))
                            }
                          >
                            {component.label}
                          </a>
                        </React.Fragment>
                      ))}
                    </p>
                  ) : (
                    <p className="support-incident__scope">{scope}</p>
                  )}
                  <p>
                    <strong>Next action:</strong>{" "}
                    {incident?.recommendedAction ||
                      "Review the affected service and its probe evidence."}
                  </p>
                  <span>
                    Observed in snapshot{" "}
                    <time dateTime={incident?.observedAt || undefined}>
                      {formatDateTime(incident?.observedAt)}
                    </time>
                  </span>
                </div>
              </li>
            );
          })}
        </ul> : <p className="support-empty-copy" role="status">No active incidents were reported.</p>}
      </div>
    </Card>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [freshnessClock, setFreshnessClock] = useState(() => Date.now());
  const { data, loading, refreshing, error, lastSuccessfulAt, refresh } =
    useSupportResource({
      load: getSupportOverview,
      resourceKey: "support-overview",
      pollIntervalMs: POLL_INTERVAL_MS,
    });

  useEffect(() => {
    const timer = window.setInterval(
      () => setFreshnessClock(Date.now()),
      10_000
    );
    return () => window.clearInterval(timer);
  }, []);

  const isStale = useMemo(() => {
    if (!data?.observedAt) return Boolean(data);
    const observedAt = parseSupportDate(data.observedAt).getTime();
    if (Number.isNaN(observedAt)) return true;
    const refreshSeconds = Number(data.refreshAfterSeconds) || 30;
    return freshnessClock - observedAt > refreshSeconds * 3 * 1000;
  }, [data, freshnessClock]);

  const pipeline = data?.pipeline || {};
  const stores = data?.stores || {};
  const evidenceAvailable = data?.dataAvailable !== false;
  const displayedPipeline = evidenceAvailable ? pipeline : {};
  const displayedStores = evidenceAvailable ? stores : {};
  const attentionStores = firstArray(data?.attentionStores);
  const recentStores = firstArray(data?.recentStores);
  const openStore = (id) => navigate(`/store/${encodeURIComponent(id)}`);

  if (loading && !data) {
    return (
      <AppPageFrame title="Operations">
        <div className="support-dashboard" aria-label="Loading operations dashboard">
          <Card><div className="support-card-content"><SkeletonBodyText lines={8} /></div></Card>
          <div className="support-metrics-grid">
            {[0, 1, 2, 3].map((item) => (
              <Card key={item}><div className="support-card-content"><SkeletonBodyText lines={3} /></div></Card>
            ))}
          </div>
        </div>
      </AppPageFrame>
    );
  }

  if (error && !data) {
    const accessDenied = isSupportAccessDenied(error);
    return (
      <AppPageFrame title="Operations">
        <Banner
          title={accessDenied ? "Support access is required" : "Operations status is unavailable"}
          status="critical"
          action={{ content: "Retry", onAction: () => refresh() }}
        >
          <p>{error}</p>
        </Banner>
      </AppPageFrame>
    );
  }

  const attentionCount = evidenceAvailable ?
    (Number(pipeline.manualReview) || 0) +
    (Number(pipeline.retryWait) || 0) +
    (Number(pipeline.failed24h) || 0) : null;

  return (
    <AppPageFrame title="Operations" maxWidth="1600px" gap="16px">
      <div className="support-dashboard">
        <Card>
          <div className="support-overview-header">
            <div>
              <div className="support-overview-header__status">
                <h2>PalSync system status</h2>
                <StatusBadge status={data?.overallStatus} />
              </div>
              <p role="status" aria-live="polite">
                {data?.summary || "No operational summary was returned."}
              </p>
              <span className="support-overview-header__freshness">
                Snapshot {formatDateTime(data?.observedAt)}
                {lastSuccessfulAt ? ` · refreshed ${formatRelativeTime(lastSuccessfulAt)}` : ""}
                {" · auto-refreshes every 30 seconds while visible"}
              </span>
            </div>
            <Button onClick={() => refresh()} loading={refreshing}>Refresh now</Button>
          </div>
        </Card>

        {error ? (
          <Banner
            title={isSupportAccessDenied(error) ? "Support access is required" : "The latest refresh failed"}
            status={isSupportAccessDenied(error) ? "critical" : "warning"}
          >
            <p>{error}. Showing the last successful snapshot.</p>
          </Banner>
        ) : null}
        {data?.dataAvailable === false ? (
          <Banner title="Operational evidence is incomplete" status="critical">
            <p>The support service could not read one or more required data sources. Treat healthy-looking counts as unknown until connectivity is restored.</p>
          </Banner>
        ) : null}
        {isStale ? (
          <Banner title="This operational snapshot is stale" status="warning">
            <p>Confirm service connectivity before relying on these values.</p>
          </Banner>
        ) : null}

        <div className="support-metrics-grid">
          <MetricCard
            label="Queued now"
            value={formatCount(displayedPipeline.queued)}
            context={`Oldest ${formatRelativeTime(displayedPipeline.oldestQueuedAt)}`}
            tone={
              displayedPipeline.queued === undefined
                ? "neutral"
                : Number(displayedPipeline.queued) > 0
                ? "warning"
                : "success"
            }
          />
          <MetricCard
            label="Processing now"
            value={formatCount(displayedPipeline.processing)}
            context={`${formatCount(displayedPipeline.throughputPerMinute)} per minute`}
          />
          <MetricCard
            label="Success rate · 24h"
            value={formatPercent(displayedPipeline.successRate24h)}
            context={`${formatCount(displayedPipeline.success24h)} successful fulfillments`}
            tone={
              displayedPipeline.successRate24h === undefined ||
              displayedPipeline.successRate24h === null
                ? "neutral"
                : Number(displayedPipeline.successRate24h) < 0.95
                ? "warning"
                : "success"
            }
          />
          <MetricCard
            label="P95 sync time"
            value={formatDuration(displayedPipeline.p95SyncMs)}
            context={`P50 ${formatDuration(displayedPipeline.p50SyncMs)} · P99 ${formatDuration(displayedPipeline.p99SyncMs)}`}
          />
          <MetricCard
            label="Needs attention"
            value={formatCount(attentionCount)}
            context={`${formatCount(displayedPipeline.manualReview)} manual · ${formatCount(displayedPipeline.retryWait)} retry · ${formatCount(displayedPipeline.blocked)} terminal`}
            tone={
              attentionCount === null
                ? "neutral"
                : attentionCount > 0
                ? "critical"
                : "success"
            }
          />
          <MetricCard
            label="Sync-ready stores"
            value={formatCount(displayedStores.healthy)}
            context={`${formatCount(displayedStores.active)} active · ${formatCount(displayedStores.total)} total`}
          />
        </div>

        <SyncEngineHealthPanel />
        <ActiveIncidents incidents={firstArray(data?.incidents)} />
        <div className="support-dashboard__two-column">
          <Components
            components={firstArray(data?.components)}
            incidents={firstArray(data?.incidents)}
          />
          <div className="support-dashboard__right-stack">
            <StoreFleet stores={displayedStores} />
            <BillingHealth
              billing={
                evidenceAvailable
                  ? { writesMode: data?.writesMode, ...(data?.billing || {}) }
                  : { writesMode: data?.writesMode }
              }
            />
          </div>
        </div>
        <AttentionStores stores={attentionStores} onOpenStore={openStore} />
        <RecentStores
          stores={recentStores}
          onOpenStore={openStore}
          onViewAll={() => navigate("/stores")}
        />
      </div>
    </AppPageFrame>
  );
};

export default Dashboard;
